// src/pages/admin/AdminSiteContent.tsx
import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore } from '../../stores/useThemeStore';

interface ContentField {
  docId: string;
  label: string;
  field: string;
  multiline: boolean;
}

const FIELDS: ContentField[] = [
  { docId: 'tagline',        label: 'Site Tagline',      field: 'text',    multiline: false },
  { docId: 'privacyPolicy',  label: 'Privacy Policy',    field: 'content', multiline: true  },
  { docId: 'termsOfService', label: 'Terms of Service',  field: 'content', multiline: true  },
];

function ContentEditor({ item, C, isDark }: { item: ContentField; C: ReturnType<typeof useThemePalette>; isDark: boolean }) {
  const [value,   setValue]   = useState('');
  const [status,  setStatus]  = useState<'idle' | 'loading' | 'saving' | 'saved' | 'error'>('loading');
  const [errMsg,  setErrMsg]  = useState('');

  useEffect(() => {
    getDoc(doc(db, 'siteContent', item.docId))
      .then(snap => {
        setValue(snap.exists() ? (snap.data()?.[item.field] as string) ?? '' : '');
        setStatus('idle');
      })
      .catch(() => setStatus('idle'));
  }, [item.docId, item.field]);

  const save = async () => {
    setStatus('saving');
    try {
      await setDoc(doc(db, 'siteContent', item.docId), { [item.field]: value }, { merge: true });
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (e: unknown) {
      setErrMsg(e instanceof Error ? e.message : 'Save failed');
      setStatus('error');
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '10px 14px',
    borderRadius: '10px',
    border: `1px solid ${C.border}`,
    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    color: C.text, fontSize: '0.85rem', fontFamily: 'inherit',
    outline: 'none', resize: 'vertical',
  };

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: '16px', padding: '24px', marginBottom: '16px',
    }}>
      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: C.text, marginBottom: '12px' }}>{item.label}</div>

      {status === 'loading' ? (
        <div style={{ color: C.muted, fontSize: '0.8rem' }}>Loading...</div>
      ) : item.multiline ? (
        <textarea
          value={value}
          onChange={e => setValue(e.target.value)}
          rows={10}
          style={{ ...inputStyle }}
        />
      ) : (
        <input
          value={value}
          onChange={e => setValue(e.target.value)}
          style={{ ...inputStyle }}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
        <button
          onClick={() => void save()}
          disabled={status === 'saving'}
          style={{
            padding: '8px 20px', borderRadius: '999px', border: 'none',
            background: C.accent, color: '#fff', fontSize: '0.78rem',
            fontWeight: 700, cursor: status === 'saving' ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}
        >
          {status === 'saving' ? 'Saving...' : 'Save'}
        </button>
        {status === 'saved'  && <span style={{ color: C.success, fontSize: '0.78rem' }}>Saved!</span>}
        {status === 'error'  && <span style={{ color: C.error,   fontSize: '0.78rem' }}>{errMsg}</span>}
      </div>
    </div>
  );
}

export default function AdminSiteContent() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  return (
    <div style={{ padding: '32px', maxWidth: '760px' }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: C.text, marginBottom: '8px' }}>Site Content</div>
      <div style={{ fontSize: '0.8rem', color: C.muted, marginBottom: '28px' }}>Edit tagline and legal pages</div>

      {FIELDS.map(item => (
        <ContentEditor key={item.docId} item={item} C={C} isDark={isDark} />
      ))}
    </div>
  );
}
